import { useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mode } from "../../types";
import { useTutorChatStore } from "../../store/useTutorChatStore";
import { useUserQuery } from "../queries/useUserQuery";
import { tutorApiService } from "../../services/tutorApi";
import { useMediaMd } from "../../../../hooks/useMedia";
import useChat from "../useChat";
import useSpeechToText from "../../../../components/speech/useSpeechToText";
import { MyContext } from "../../../../context/MyContext";
import { useAuth } from "../useAuth";
import getUserState from "../../utils/getUserState";

/**
 * useChatInputView
 *
 * Prompt 입력 컴포넌트와 스트리밍 로직을 연결하는 presentation hook
 *
 * 기능:
 * - 사용자 입력 제출 처리
 * - 스트리밍 시작 및 실시간 청크 처리
 * - 스트리밍 중단 처리
 * - 음성 입력 (STT) 처리 및 티켓 검증
 * - 음성 인식 완료 후 자동 메시지 전송
 * - Prompt 컴포넌트 props 제공
 */

interface UseChatInputViewOptions {
  mode: Mode;
  stopTts?: () => void;
}

export const useChatInputView = (options: UseChatInputViewOptions) => {
  const { mode, stopTts } = options;
  const isMd = useMediaMd();
  const location = useLocation();
  const navigate = useNavigate();
  const { redirectToLogin } = useAuth();

  // Local state
  const [inputValue, setInputValue] = useState("");

  // URL 쿼리 파라미터로 입력 폼 자동완성 (q 또는 query 지원)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryText = queryParams.get("query") || queryParams.get("q");

    if (queryText) {
      setInputValue(decodeURIComponent(queryText));
    }
  }, [location.search]);

  // Context (TODO: setIsOpenTicketAlert를 useUIStore로 마이그레이션)
  const { setIsOpenTicketAlert } = useContext(MyContext);

  // Hooks
  const { user, isLoggedIn } = useUserQuery();
  const {
    fetching,
    streamingMessageId,
    abortController,
    abortStreamingMessage,
    cancelStreaming,
  } = useTutorChatStore();

  const { submitMessage, isCheckingDevice } = useChat(mode);

  // Ticket validation - 세 가지 조건 확인:
  // (1) 이용권 소진 시
  // (2) 무료 횟수 소진 시 (paid가 false이고 카운트가 0)
  // (3) 이용권 기간 종료 시 (end_dt 체크)
  const isSocrates = mode === Mode.SOCRATES;
  const ticketCount = isSocrates ? user?.socra_cnt : user?.tutor_cnt;
  const userState = getUserState(user);

  // 조건 (1): 이용권 소진 - 카운트가 0 이하
  const isTicketExhausted = !ticketCount || ticketCount <= 0;

  // 조건 (2): 무료 횟수 소진 - 미구독 상태(paid: false)에서 카운트가 0
  const isFreeTrialExhausted = !user?.paid && isTicketExhausted;

  // 조건 (3): 이용권 기간 종료 - userState가 'expired'
  const isSubscriptionExpired = userState === "expired";

  // 세 가지 조건 중 하나라도 해당되면 서비스 이용 불가
  const isPossibleToUseService =
    !isTicketExhausted && !isFreeTrialExhausted && !isSubscriptionExpired;

  const handleSubmit = () => {
    // TTS 재생 중이면 중단
    stopTts?.();

    // 특정 입력값에 대한 페이지 이동 처리
    if (inputValue.trim() === "구독 테스트 페이지 이동하기") {
      navigate("/subscription/test");
      setInputValue("");
      return;
    }

    submitMessage(inputValue, {
      callback: () => {
        setInputValue("");
      },
    });
  };

  /**
   * 스트리밍 중단
   */
  const handleStop = () => {
    if (abortController) {
      // HTTP 요청 중단
      tutorApiService.cancelStreaming();

      // 메시지 상태 업데이트
      if (streamingMessageId) {
        abortStreamingMessage(
          streamingMessageId,
          "사용자가 응답 생성을 중단했습니다.",
        );
      }

      // 전역 상태 정리
      cancelStreaming();
    }
  };

  /**
   * 입력값 변경 처리
   */
  const handleChange = (value: string) => {
    setInputValue(value);
  };

  // Speech-to-Text hook (handleSpeechComplete보다 먼저 선언)
  const {
    listening,
    startListening,
    stopListening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechToText({
    onUpdateTranscript: (text: string) => {
      // 실시간 transcript를 inputValue로 업데이트
      setInputValue(text);
    },
    onStopListening: (text: string) => {
      // 음성 인식 완료 시 자동 메시지 전송
      if (text.trim()) {
        // TTS 재생 중이면 중단
        stopTts?.();

        submitMessage(text, {
          callback: () => {
            resetTranscript();
            setInputValue("");
          },
        });
      }
    },
  });

  /**
   * 마이크 버튼 클릭 처리
   * 티켓 검증 후 음성 인식 시작
   */
  const handleMicClick = () => {
    if (!isLoggedIn) return;

    if (!isPossibleToUseService) {
      setIsOpenTicketAlert(true);
      return;
    }

    if (!browserSupportsSpeechRecognition) {
      console.error("Browser does not support speech recognition");
      return;
    }

    startListening();
  };

  /**
   * 음성 인식 닫기/중단 처리
   */
  const handleCloseClick = () => {
    stopListening();
    resetTranscript();
    setInputValue("");
  };

  /**
   * Prompt 상태 결정
   */
  const getPromptState = (): "default" | "typing" | "listening" | "loading" => {
    // 기기 등록 판정 왕복 동안에도 로딩으로 처리해야 연타 전송을 막을 수 있다
    if (fetching || isCheckingDevice) return "loading";
    if (listening) return "listening";
    if (inputValue.length > 0) return "typing";
    return "default";
  };

  const showPrompt = isLoggedIn || isMd;
  const showLoginButton = !isMd;

  return {
    // Prompt props
    promptProps: {
      state: getPromptState(),
      value: inputValue,
      placeholder: "무엇이든 물어보세요.",
      onSubmit: handleSubmit,
      onChange: handleChange,
      onStopClick: handleStop,
      onMicClick: handleMicClick,
      onCloseClick: handleCloseClick,
      disabled: !isLoggedIn || fetching || listening || isCheckingDevice,
    },

    // State
    inputValue,
    isStreaming: fetching,
    isListening: listening,
    showPrompt,
    showLoginButton,

    // Actions
    handleSubmit,
    handleStop,
    handleChange,
    handleMicClick,
    handleCloseClick,
    handleLoginClick: redirectToLogin,
  };
};
